import React from 'react';
import { Tabs, Card, Typography, Button, Space, Tag, Tooltip, Drawer, Empty, List, Popconfirm } from 'antd';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import ReportFormatForm from './Form';
import ReportNodeList from '../../report-nodes/views/List';
import AllQuestions from './AllQuestions';
import {
  clearReportFormatControlAreasFromCompleteQuestions,
  createMissingControlAreaQuestionClones,
  getReportFormat,
  getReportFormatControlAreaCompleteQuestions,
  getReportFormatControlAreas,
  getMissingControlAreaQuestionClonePreview,
} from '../services/report-formatService';
import type {
  ReportFormatControlArea,
  ReportFormatControlAreaCompleteQuestionAudit,
  ReportFormatMissingControlAreaQuestionClonePreview,
} from '../types/ReportFormat';
import { openNotification } from '../../../utils';

const getActiveTabFromUrl = (tab: string | null): string => {
  if (tab === '2' || tab === 'questions') {
    return 'tab2';
  }
  if (tab === '1' || tab === 'structure') {
    return 'tab1';
  }
  return 'tab0';
};

const getUrlTabFromActiveKey = (key: string): string => {
  if (key === 'tab2') return '2';
  return key === 'tab1' ? '1' : '0';
};

export default function ReportFormatDetail(){
  const { id } = useParams();
  const reportFormatId = Number(id);
  const [attributeName, setAttributeName] = React.useState<string>('');
  const [attributeId, setAttributeId] = React.useState<number | undefined>(undefined);
  const [reportFormatPublicUuid, setReportFormatPublicUuid] = React.useState<string | undefined>(undefined);
  const [accreditationLogoUrl, setAccreditationLogoUrl] = React.useState<string | null>(null);
  const [controlAreas, setControlAreas] = React.useState<ReportFormatControlArea[]>([]);
  const [completeQuestionAudit, setCompleteQuestionAudit] = React.useState<ReportFormatControlAreaCompleteQuestionAudit | null>(null);
  const [completeQuestionDrawerOpen, setCompleteQuestionDrawerOpen] = React.useState(false);
  const [completeQuestionLoading, setCompleteQuestionLoading] = React.useState(false);
  const [clearingControlAreas, setClearingControlAreas] = React.useState(false);
  const [missingClonePreview, setMissingClonePreview] = React.useState<ReportFormatMissingControlAreaQuestionClonePreview | null>(null);
  const [missingCloneDrawerOpen, setMissingCloneDrawerOpen] = React.useState(false);
  const [missingCloneLoading, setMissingCloneLoading] = React.useState(false);
  const [creatingMissingClones, setCreatingMissingClones] = React.useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeKey = React.useMemo(() => getActiveTabFromUrl(searchParams.get('tab')), [searchParams]);

  const loadControlAreas = React.useCallback(async () => {
    if (!reportFormatId) {
      setControlAreas([]);
      return;
    }

    try {
      const items = await getReportFormatControlAreas(reportFormatId);
      setControlAreas(Array.isArray(items) ? items : []);
    } catch {
      setControlAreas([]);
    }
  }, [reportFormatId]);

  React.useEffect(() => {
    if (!reportFormatId) return;
    getReportFormat(reportFormatId)
      .then((rf: any) => { 
        setAttributeName(rf?.attribute?.name || '');
        const aid = typeof rf?.attribute === 'object' ? rf?.attribute?.id : rf?.attribute;
        setAttributeId(aid);
        setReportFormatPublicUuid(typeof rf?.publicUuid === 'string' ? rf.publicUuid : undefined);
        const logo = typeof rf?.accreditationLogoUrl === 'string' ? rf.accreditationLogoUrl.trim() : '';
        setAccreditationLogoUrl(logo.length ? logo : null);
      })
      .catch(() => {
        setAttributeName('');
        setReportFormatPublicUuid(undefined);
        setAccreditationLogoUrl(null);
      });
  }, [reportFormatId]);

  React.useEffect(() => {
    void loadControlAreas();
  }, [loadControlAreas]);

  const openCompleteQuestionDrawer = React.useCallback(async () => {
    if (!reportFormatId) return;

    setCompleteQuestionDrawerOpen(true);
    setCompleteQuestionLoading(true);
    try {
      const audit = await getReportFormatControlAreaCompleteQuestions(reportFormatId);
      setCompleteQuestionAudit(audit);
    } catch {
      setCompleteQuestionAudit(null);
      openNotification('error', 'Hata', 'Tüm kontrol alanlı sorular yüklenemedi.');
    } finally {
      setCompleteQuestionLoading(false);
    }
  }, [reportFormatId]);

  const clearCompleteQuestionControlAreas = React.useCallback(async () => {
    if (!reportFormatId) return;

    setClearingControlAreas(true);
    try {
      const cleanupResponse = await clearReportFormatControlAreasFromCompleteQuestions(reportFormatId);
      const refreshedAudit = await getReportFormatControlAreaCompleteQuestions(reportFormatId);
      setCompleteQuestionAudit(refreshedAudit);
      await loadControlAreas();
      openNotification(
        'success',
        'Başarılı',
        `${cleanupResponse.clearedQuestionCount} sorudan kontrol alanları kaldırıldı.`,
      );
    } catch {
      openNotification('error', 'Hata', 'Kontrol alanları kaldırılamadı.');
    } finally {
      setClearingControlAreas(false);
    }
  }, [loadControlAreas, reportFormatId]);

  const openMissingCloneDrawer = React.useCallback(async () => {
    if (!reportFormatId) return;

    setMissingCloneDrawerOpen(true);
    setMissingCloneLoading(true);
    try {
      const preview = await getMissingControlAreaQuestionClonePreview(reportFormatId);
      setMissingClonePreview(preview);
    } catch {
      setMissingClonePreview(null);
      openNotification('error', 'Hata', 'Eksik kontrol alanlı sorular yüklenemedi.');
    } finally {
      setMissingCloneLoading(false);
    }
  }, [reportFormatId]);

  const createMissingClones = React.useCallback(async () => {
    if (!reportFormatId) return;

    setCreatingMissingClones(true);
    try {
      const creationResponse = await createMissingControlAreaQuestionClones(reportFormatId);
      const refreshedPreview = await getMissingControlAreaQuestionClonePreview(reportFormatId);
      setMissingClonePreview(refreshedPreview);
      await loadControlAreas();
      openNotification(
        'success',
        'Başarılı',
        `${creationResponse.createdQuestionCount} soru klonlanarak eklendi.`,
      );
    } catch {
      openNotification('error', 'Hata', 'Eksik kontrol alanlı sorular eklenemedi.');
    } finally {
      setCreatingMissingClones(false);
    }
  }, [loadControlAreas, reportFormatId]);

  const renderControlAreaTag = (controlArea: ReportFormatControlArea) => {
    const isPassive = controlArea.isActive === false;
    const tag = (
      <Tag
        key={controlArea.id}
        color={isPassive ? 'default' : 'blue'}
        aria-disabled={isPassive}
        style={isPassive ? { cursor: 'not-allowed', opacity: 0.55 } : undefined}
      >
        {controlArea.name}
      </Tag>
    );

    return isPassive ? (
      <Tooltip key={controlArea.id} title='Pasif kontrol alanı'>
        {tag}
      </Tooltip>
    ) : tag;
  };

  const handleTabChange = React.useCallback(
    (key: string) => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('tab', getUrlTabFromActiveKey(key));
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const items = [
    { key: 'tab0', label: 'Rapor Bilgileri', children: <ReportFormatForm id={reportFormatId} onSuccess={() => {}} /> },
    { key: 'tab1', label: 'Rapor Yapısı', children: <ReportNodeList referenceId={reportFormatId} reportFormatPublicUuid={reportFormatPublicUuid} reportAttributeId={attributeId} /> },
    { key: 'tab2', label: 'Tüm Sorular', children: <AllQuestions reportFormatId={reportFormatId} reportAttributeId={attributeId} /> },
  ];
  return (
    <Card
      className='br-30px'
      title={<Typography.Title level={4} style={{ margin: 0 }}>{attributeName || 'Rapor Formatı'}</Typography.Title>}
      extra={attributeId ? (
        <Space wrap size='small' style={{ justifyContent: 'flex-end', maxWidth: 'min(70vw, 720px)' }}>
          {controlAreas.length ? (
            <Space wrap size={[4, 4]}>
              <Typography.Text type='secondary'>Kontrol Alanları ({controlAreas.length}):</Typography.Text>
              {controlAreas.map(renderControlAreaTag)}
            </Space>
          ) : null}
          <Button type='default' onClick={() => void openMissingCloneDrawer()}>
            Eksik Kontrol Alanlı Soruları Ekle
          </Button>
          <Button type='default' onClick={() => void openCompleteQuestionDrawer()}>
            Tüm Kontrol Alanlı Sorular
          </Button>
          <Link to={`/report-formats/${attributeId}`}>
            <Button type='default' className='d-button action-button'>Geri</Button>
          </Link>
        </Space>
      ) : undefined}
    >
      <Tabs items={items} activeKey={activeKey} onChange={handleTabChange} />
      <Drawer
        title='Tüm Kontrol Alanlarının Eklendiği Sorular'
        open={completeQuestionDrawerOpen}
        onClose={() => setCompleteQuestionDrawerOpen(false)}
        destroyOnClose
        width={720}
        footer={
          <Popconfirm
            title={`Bu işlem listelenen ${completeQuestionAudit?.questions.length ?? 0} sorudan tüm kontrol alanlarını kaldırır. Devam etmek istiyor musunuz?`}
            okText='Kaldır'
            cancelText='Vazgeç'
            onConfirm={() => void clearCompleteQuestionControlAreas()}
            disabled={!completeQuestionAudit?.questions.length || clearingControlAreas}
          >
            <Button
              danger
              type='primary'
              loading={clearingControlAreas}
              disabled={!completeQuestionAudit?.questions.length || clearingControlAreas}
            >
              Kontrol Alanlarını Kaldır
            </Button>
          </Popconfirm>
        }
      >
        <Typography.Paragraph type='secondary'>
          Rapor formatında {completeQuestionAudit?.controlAreas.length ?? 0} kontrol alanı bulunuyor.
        </Typography.Paragraph>
        {completeQuestionLoading ? (
          <List loading />
        ) : !completeQuestionAudit?.controlAreas.length ? (
          <Empty description='Rapor formatında kontrol alanı bulunamadı.' />
        ) : !completeQuestionAudit.questions.length ? (
          <Empty description='Tüm kontrol alanlarının eklendiği soru bulunamadı.' />
        ) : (
          <List
            dataSource={completeQuestionAudit.questions}
            renderItem={(question) => (
              <List.Item>
                <Space direction='vertical' size={4} style={{ width: '100%' }}>
                  <Typography.Text strong>{question.title}</Typography.Text>
                  <Typography.Text type='secondary'>
                    Rapor düğümü: {question.nodeTitle || 'Belirtilmemiş'}
                  </Typography.Text>
                  <Space wrap size={[4, 4]}>
                    {question.controlAreas.map(renderControlAreaTag)}
                  </Space>
                </Space>
              </List.Item>
            )}
          />
        )}
      </Drawer>
      <Drawer
        title='Eksik Kontrol Alanlı Sorular'
        open={missingCloneDrawerOpen}
        onClose={() => setMissingCloneDrawerOpen(false)}
        destroyOnClose
        width={720}
        footer={
          <Popconfirm
            title={`Bu işlem ${missingClonePreview?.cloneCount ?? 0} soru klonu ekler. Devam etmek istiyor musunuz?`}
            okText='Ekle'
            cancelText='Vazgeç'
            onConfirm={() => void createMissingClones()}
            disabled={!missingClonePreview?.cloneCount || creatingMissingClones}
          >
            <Button
              type='primary'
              loading={creatingMissingClones}
              disabled={!missingClonePreview?.cloneCount || creatingMissingClones}
            >
              Soruları Ekle
            </Button>
          </Popconfirm>
        }
      >
        <Typography.Paragraph type='secondary'>
          {missingClonePreview?.cloneCount ?? 0} soru klonlanarak eklenecek.
        </Typography.Paragraph>
        {missingCloneLoading ? (
          <List loading />
        ) : !missingClonePreview?.controlAreas.length ? (
          <Empty description='Rapor formatında kontrol alanı bulunamadı.' />
        ) : !missingClonePreview.cloneCount ? (
          <Empty description='Eksik kontrol alanı bulunan soru yok.' />
        ) : (
          <List
            dataSource={missingClonePreview.candidates}
            renderItem={(candidate) => (
              <List.Item>
                <Space direction='vertical' size={4} style={{ width: '100%' }}>
                  <Typography.Text strong>{candidate.title}</Typography.Text>
                  <Typography.Text type='secondary'>
                    Rapor düğümü: {candidate.nodeTitle || 'Belirtilmemiş'}
                  </Typography.Text>
                  <Space wrap size={[4, 4]}>
                    <Typography.Text type='secondary'>Mevcut:</Typography.Text>
                    {candidate.currentControlAreas.map(renderControlAreaTag)}
                  </Space>
                  <Space wrap size={[4, 4]}>
                    <Typography.Text type='secondary'>Eklenecek:</Typography.Text>
                    {candidate.missingControlAreas.map(renderControlAreaTag)}
                  </Space>
                  {candidate.missingOutOfScopeAnswer ? (
                    <Typography.Text type='warning'>
                      “Kapsam dışı” cevabı bulunmadığı için varsayılan cevap seçilmeyecek.
                    </Typography.Text>
                  ) : null}
                </Space>
              </List.Item>
            )}
          />
        )}
      </Drawer>
    </Card>
  );
}
